// Typed transport for the public widget API. No storage, no retries, no
// policy: it turns HTTP into typed results and errors, and session.ts
// decides what to do with a 401.

import type {
  AttachmentInput,
  Conversation,
  ConversationDetail,
  CreateConversationResponse,
  Message,
  PublicConfig,
  SessionGrant,
  UploadTicket,
  Visitor,
} from './types'

const DEFAULT_API_BASE = 'https://api.airys.ai'

// ApiError carries the status and the API's `error` code so callers can
// branch on data instead of matching message text.
export class ApiError extends Error {
  status: number
  code: string
  constructor(status: number, code: string, message?: string) {
    super(message ?? `${status} ${code}`)
    this.status = status
    this.code = code
  }
}

// NetworkError means the request never got an answer (offline, CORS, DNS).
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('widget: network error')
    this.cause = cause
  }
}

export function isApiError(err: unknown, status?: number, code?: string): err is ApiError {
  if (!(err instanceof ApiError)) return false
  if (status !== undefined && err.status !== status) return false
  if (code !== undefined && err.code !== code) return false
  return true
}

export interface CreateConversationInput {
  clientId: string
  content: string
  sourceUrl?: string
  userJwt?: string
  /** Used only when there is no `userJwt`: the token always wins. */
  visitor?: Visitor
}

export interface Api {
  getConfig(clientId: string): Promise<PublicConfig>
  createConversation(input: CreateConversationInput): Promise<CreateConversationResponse>
  /** A verified session; bound to `conversationId` when the visitor owns it. */
  mintSession(clientId: string, userJwt: string, conversationId?: string): Promise<SessionGrant>
  /** Extends an idle session that is still inside its absolute lifetime. */
  refreshSession(token: string): Promise<Pick<SessionGrant, 'expiresAt' | 'absoluteExpiresAt'>>
  getConversation(token: string, id: string): Promise<ConversationDetail>
  pollMessages(token: string, id: string, since?: string): Promise<Message[]>
  sendMessage(token: string, id: string, content: string, attachments?: AttachmentInput[]): Promise<Message>
  /**
   * A presigned PUT for one file; the bytes go straight to storage. `sizeBytes`
   * is signed into the URL, so storage rejects a body of any other length.
   */
  requestUpload(token: string, id: string, mimeType: string, sizeBytes: number): Promise<UploadTicket>
  putFile(url: string, file: Blob, mimeType: string): Promise<void>
  submitCsat(token: string, id: string, satisfied: boolean, comment?: string): Promise<Conversation>
  listHistory(token: string): Promise<Conversation[]>
  getHistoryConversation(token: string, id: string): Promise<ConversationDetail>
  /** EventSource can't send headers, so the stream takes the token as a query param. */
  streamUrl(token: string, id: string): string
}

export function createApi(apiBase?: string): Api {
  const base = (apiBase ?? DEFAULT_API_BASE).replace(/\/+$/, '')

  async function request<T>(
    path: string,
    init: { method?: string; body?: unknown; token?: string } = {},
  ): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' }
    if (init.body !== undefined) headers['content-type'] = 'application/json'
    if (init.token) headers['authorization'] = `Bearer ${init.token}`
    let res: Response
    try {
      res = await fetch(`${base}${path}`, {
        method: init.method ?? 'GET',
        headers,
        body: init.body === undefined ? undefined : JSON.stringify(init.body),
      })
    } catch (err) {
      throw new NetworkError(err)
    }
    if (!res.ok) throw await toApiError(res)
    if (res.status === 204) return undefined as T
    return (await res.json()) as T
  }

  const q = encodeURIComponent

  return {
    getConfig: (clientId) => request(`/v1/widget/config?clientId=${q(clientId)}`),
    createConversation: (input) => request('/v1/conversations', { method: 'POST', body: input }),
    mintSession: (clientId, userJwt, conversationId) =>
      request(`/v1/clients/${q(clientId)}/sessions`, {
        method: 'POST',
        body: { userJwt, conversationId },
      }),
    refreshSession: (token) => request('/v1/sessions/refresh', { method: 'POST', token }),
    getConversation: (token, id) => request(`/v1/conversations/${q(id)}`, { token }),
    pollMessages: async (token, id, since) => {
      const query = since ? `?since=${q(since)}` : ''
      return (await request<Message[] | null>(`/v1/conversations/${q(id)}/poll${query}`, { token })) ?? []
    },
    sendMessage: (token, id, content, attachments) =>
      request(`/v1/conversations/${q(id)}/messages`, {
        method: 'POST',
        body: attachments?.length ? { content, attachments } : { content },
        token,
      }),
    requestUpload: (token, id, mimeType, sizeBytes) =>
      request(`/v1/conversations/${q(id)}/uploads`, {
        method: 'POST',
        body: { mimeType, sizeBytes },
        token,
      }),
    // Storage checks the Content-Type against what the URL was signed for.
    putFile: async (url, file, mimeType) => {
      let res: Response
      try {
        res = await fetch(url, { method: 'PUT', headers: { 'content-type': mimeType }, body: file })
      } catch (err) {
        throw new NetworkError(err)
      }
      if (!res.ok) throw new ApiError(res.status, 'upload_failed', 'upload failed')
    },
    submitCsat: (token, id, satisfied, comment) =>
      request(`/v1/conversations/${q(id)}/csat`, {
        method: 'POST',
        body: { satisfied, comment: comment || undefined },
        token,
      }),
    // The API answers `null` instead of `[]` when there is nothing.
    listHistory: async (token) =>
      (await request<Conversation[] | null>('/v1/conversations/history', { token })) ?? [],
    getHistoryConversation: (token, id) =>
      request(`/v1/conversations/history/${q(id)}`, { token }),
    streamUrl: (token, id) => `${base}/v1/conversations/${q(id)}/stream?session=${q(token)}`,
  }
}

async function toApiError(res: Response): Promise<ApiError> {
  let code = ''
  let message: string | undefined
  try {
    const body = (await res.json()) as { error?: unknown; message?: unknown }
    if (typeof body.error === 'string') code = body.error
    if (typeof body.message === 'string') message = body.message
  } catch {
    // Not JSON; status alone has to do.
  }
  return new ApiError(res.status, code, message ?? (code || res.statusText))
}
